import BottomBar from './BottomBar';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { useAdvSettlementRows, useDetailRows, useTaxRows } from './hooks';

import { AritoFormV2, HeaderTabsV2 } from '@/components/custom/erp';
import { FormSchema, initialFormValues } from '../../schema';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useAuth } from '@/contexts/auth-context';
import AdvSettlementTab from './AdvSettlementTab';
import { useFormFieldState } from '../../hooks';
import InfoDropdown from './InfoDropdown';
import BasicInfoTab from './BasicInfoTab';
import { FormMode } from '@/types/form';
import HistoryTab from './HistoryTab';

import DetailTab from './DetailTab';
import { useToast } from '@/hooks';
import OtherTab from './OtherTab';
import TaxTab from './TaxTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('info');

  const { state, actions } = useFormFieldState(initialData);
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handlePasteRow: detailHandlePasteRow,
    handleCopyRow: detailHandleCopyRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: setDetailNonWritableFields,
    setPasteConfigs: setDetailPasteConfigs,
    updateDienGiaiForEmptyRows,
    isPasting: detailIsPasting
  } = useDetailRows(initialData?.chi_tiet_phieu_thanh_toan_data || [], state.dienGiai);
  const {
    rows: advSettlementRows,
    selectedRowUuid: advSettlementSelectedRowUuid,
    selectedCell: advSettlementSelectedCell,
    handleRowClick: advSettlementHandleRowClick,
    handleCellClick: advSettlementHandleCellClick,
    handleAddRow: advSettlementHandleAddRow,
    handleDeleteRow: advSettlementHandleDeleteRow,
    handleCopyRow: advSettlementHandleCopyRow,
    handlePasteRow: advSettlementHandlePasteRow,
    handleMoveRow: advSettlementHandleMoveRow,
    handleCellValueChange: advSettlementHandleCellValueChange,
    setNonWritableFields: setAdvSettlementNonWritableFields,
    setPasteConfigs: setAdvSettlementPasteConfigs,
    isPasting: advSettlementIsPasting
  } = useAdvSettlementRows(initialData?.quyet_toan_tam_ung_data || []);
  const {
    rows: taxRows,
    setRows: setTaxRows,
    selectedRowUuid: taxSelectedRowUuid,
    selectedCell: taxSelectedCell,
    handleRowClick: taxHandleRowClick,
    handleCellClick: taxHandleCellClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange,
    setNonWritableFields: setTaxNonWritableFields,
    setPasteConfigs: setTaxPasteConfigs
  } = useTaxRows(initialData?.thue_phieu_thanh_toan_data || []);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });
  const handleRepeatTaxRows = useRepeatRowsHandler({
    setRows: setTaxRows,
    markDirty: () => undefined
  });

  const totals = useMemo(() => {
    return calculateTotals(detailRows, taxRows);
  }, [detailRows, taxRows]);

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  useEffect(() => {
    if (formMode === 'add' && detailRows.length === 0) {
      detailHandleAddRow();
    }
  }, [formMode, detailRows.length, detailHandleAddRow]);

  const prevDienGiaiRef = useRef<string>('');

  useEffect(() => {
    if (state.dienGiai && state.dienGiai !== prevDienGiaiRef.current) {
      prevDienGiaiRef.current = state.dienGiai;
      updateDienGiaiForEmptyRows(state.dienGiai);
    }
  }, [state.dienGiai, updateDienGiaiForEmptyRows]);

  const handleSubmit = async (data: MuaHangRecord) => {
    const mode = formMode === 'add' ? 'add' : formMode === 'edit' ? 'edit' : 'view';
    const formData = transformFormData({
      data,
      state,
      detailRows,
      taxRows,
      advSettlementRows,
      entityUnit,
      initialData,
      formMode: mode,
      includeEmptyArrays: false
    });

    const finalFormData = {
      ...formData,
      t_tien_nt: totals.totalAmount,
      t_tien: totals.totalAmount,
      t_thue_nt: totals.totalTax,
      t_thue: totals.totalTax,
      t_tt_nt: totals.totalPayment,
      t_tt: totals.totalPayment,
      t_tien_cl_nt: totals.totalAmount,
      t_tien_cl: totals.totalPayment
    };

    try {
      await onSubmit?.(finalFormData);
    } catch (error: MuaHangRecord) {
      console.error('Submit failed:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu thanh toán tạm ứng'
        schema={FormSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={handleClose}
        headerFields={
          <div>
            <HeaderTabsV2
              tabs={[
                {
                  id: 'info',
                  label: 'Thông tin',
                  component: <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
                },
                ...(formMode === 'view'
                  ? [
                      {
                        id: 'history',
                        label: 'Lịch sử',
                        component: <HistoryTab />
                      }
                    ]
                  : [])
              ]}
              onTabChange={handleTabChange}
              defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'history' ? 1 : 0}
              actionButtons={
                formMode !== 'add' &&
                initialData?.uuid && <InfoDropdown uuid={initialData.uuid} ma_ct={initialData?.chung_tu} />
              }
            />
          </div>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  onRepeatForRows={handleRepeatDetailRows}
                  handleUpdateNonWritableFields={setDetailNonWritableFields}
                  handleUpdatePasteConfigs={setDetailPasteConfigs}
                  isPasting={detailIsPasting}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <TaxTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  selectedCell={taxSelectedCell}
                  onRowClick={taxHandleRowClick}
                  onCellClick={taxHandleCellClick}
                  onAddRow={taxHandleAddRow}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onMoveRow={taxHandleMoveRow}
                  onCellValueChange={taxHandleCellValueChange}
                  onRepeatForRows={handleRepeatTaxRows}
                />
              )
            },
            ...(state.quyetToanTamUng
              ? [
                  {
                    id: 'adv-settlement',
                    label: 'Quyết toán các lần tạm ứng',
                    component: (
                      <AdvSettlementTab
                        formMode={formMode}
                        rows={advSettlementRows}
                        selectedRowUuid={advSettlementSelectedRowUuid}
                        selectedCell={advSettlementSelectedCell}
                        onRowClick={advSettlementHandleRowClick}
                        onCellClick={advSettlementHandleCellClick}
                        onAddRow={advSettlementHandleAddRow}
                        onDeleteRow={advSettlementHandleDeleteRow}
                        onCopyRow={advSettlementHandleCopyRow}
                        onPasteRow={advSettlementHandlePasteRow}
                        onMoveRow={advSettlementHandleMoveRow}
                        onCellValueChange={advSettlementHandleCellValueChange}
                        handleUpdateNonWritableFields={setAdvSettlementNonWritableFields}
                        handleUpdatePasteConfigs={setAdvSettlementPasteConfigs}
                        isPasting={advSettlementIsPasting}
                      />
                    )
                  }
                ]
              : []),
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={
          <BottomBar
            totalAmount={totals.totalAmount}
            totalTax={totals.totalTax}
            totalPayment={totals.totalPayment}
            formState={{ state, actions }}
          />
        }
      />
    </>
  );
};

export default FormDialog;
